import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi } from "@/lib/standup-api";
import DashboardSidebar from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Cube, Trash, Check, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

// Frame 1.3 — Manage Meetings (Quick Edit pane on the right).
//
// "Advanced Setup Options" routes the user back to the full /standup/new
// wizard with prefilled state so they can edit the heavier knobs (background,
// templates, host features). Quick-edit covers name/cadence/time/interval.
export default function StandUpManage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [standups, setStandups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    standupApi.listMine().then((r) => {
      setStandups(r.data.standups || []);
      if (r.data.standups?.length && !selectedId) setSelectedId(r.data.standups[0].standup_id);
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedId) { setEditing(null); return; }
    const s = standups.find((x) => x.standup_id === selectedId);
    setEditing(s ? { ...s } : null);
  }, [selectedId, standups]);

  async function save() {
    if (!editing) return;
    try {
      const r = await standupApi.update(editing.standup_id, {
        name: editing.name,
        cadence: editing.cadence,
        recurrence: editing.recurrence,
      });
      setStandups((arr) => arr.map((s) => (s.standup_id === r.data.standup_id ? r.data : s)));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  }

  async function remove() {
    if (!editing) return;
    if (!window.confirm(`Delete "${editing.name}"? This cannot be undone.`)) return;
    try {
      await standupApi.remove(editing.standup_id);
      setStandups((arr) => arr.filter((s) => s.standup_id !== editing.standup_id));
      setSelectedId(null);
      toast.success("Standup deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  const filtered = standups.filter((s) => !filter || s.name?.toLowerCase().includes(filter.toLowerCase()));

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex bg-white" data-testid="standup-manage">
      <DashboardSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        user={user}
        onNewProject={() => navigate("/dashboard")}
        onOpenMilestones={() => navigate("/dashboard")}
        onOpenSupport={() => navigate("/dashboard")}
        activeItem="standup"
      />
      <main className="flex-1 px-6 py-10 bg-[#FAFAFC] overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/standup")} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-home">
              <ArrowLeft size={12} weight="bold" /> Back To StandUp Home
            </button>
            <Button onClick={() => navigate("/standup/new")} className="bg-slate-950 hover:bg-slate-800 text-white text-xs h-9" data-testid="new-meeting-btn">
              <Plus size={11} weight="bold" className="mr-1" /> New Meeting
            </Button>
          </div>

          <h1 className="mt-12 text-3xl font-bold text-slate-900">Manage <span className="text-sky-500">your meetings</span></h1>
          <p className="mt-1 text-sm text-slate-500">Update names, cadences, times, and rosters for any standups you've created.</p>

          <div className="mt-8 grid sm:grid-cols-[280px,1fr] gap-6">
            {/* List */}
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="relative">
                <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search Meetings…" className="h-9 pl-8 text-sm" data-testid="search-meetings" />
              </div>
              <div className="mt-3 flex items-center justify-between px-1">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Your Meetings</span>
                <span className="text-[10px] text-slate-400">({filtered.length})</span>
              </div>
              <div className="mt-1 space-y-1 max-h-[420px] overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-2 py-6 text-xs text-slate-400 text-center" data-testid="manage-empty">
                    No standups yet. Hit "+ New Meeting" to get started.
                  </div>
                ) : (
                  filtered.map((s, i) => {
                    const letter = String.fromCharCode(65 + (i % 26));
                    const active = s.standup_id === selectedId;
                    return (
                      <button
                        key={s.standup_id}
                        onClick={() => setSelectedId(s.standup_id)}
                        className={`w-full px-2 py-2 rounded-md flex items-center gap-3 text-left ${active ? "bg-sky-50/70 ring-1 ring-sky-200" : "hover:bg-slate-50"}`}
                        data-testid={`manage-row-${s.standup_id}`}
                      >
                        <span className="h-9 w-9 rounded-md text-white text-sm font-bold grid place-items-center" style={{ background: hashGradient(s.standup_id) }}>
                          {letter}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{s.name}</div>
                          <div className="text-[11px] text-slate-500 capitalize">{s.cadence} · {(s.participants || []).length - 1} invited</div>
                        </div>
                        {active && <Check size={12} weight="bold" className="text-sky-500" />}
                      </button>
                    );
                  })
                )}
              </div>
              <Button onClick={() => navigate("/standup/new")} className="mt-3 w-full h-9 bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="advanced-setup-btn">
                Advanced Setup Options →
              </Button>
            </div>

            {/* Quick Edit pane */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Meeting Details (Quick Edit)</div>
              {!editing ? (
                <div className="mt-6 text-sm text-slate-400">Pick a standup on the left to edit.</div>
              ) : (
                <>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-10 w-10 rounded-md bg-teal-100 grid place-items-center">
                      <Cube size={16} weight="bold" className="text-teal-700" />
                    </span>
                    <div>
                      <div className="text-[10px] text-slate-500">Editing</div>
                      <div className="text-sm font-semibold text-slate-900">{editing.name}</div>
                    </div>
                  </div>

                  <div className="mt-5 space-y-4 text-sm">
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Meeting Name</label>
                      <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 h-9" data-testid="edit-name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Cadence</label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        {["daily", "weekly", "custom"].map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setEditing({ ...editing, cadence: c })}
                            className={`text-xs font-semibold capitalize h-8 rounded-md border ${editing.cadence === c ? "border-violet-500 bg-violet-50/60" : "border-slate-200"}`}
                            data-testid={`edit-cadence-${c}`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500">Time</label>
                        <Input
                          type="time"
                          value={editing.recurrence?.time || "08:00"}
                          onChange={(e) => setEditing({ ...editing, recurrence: { ...(editing.recurrence || {}), time: e.target.value } })}
                          className="mt-1 h-9"
                          data-testid="edit-time"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">Next Session</label>
                        <Input value={editing.recurrence?.next_session || ""} placeholder="Auto" readOnly className="mt-1 h-9 bg-slate-50" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500">Interval</label>
                      <div className="mt-1 flex items-center gap-2">
                        <ArrowsClockwise size={14} className="text-slate-400" />
                        <Input
                          type="number"
                          min={1}
                          value={editing.recurrence?.interval_n || 1}
                          onChange={(e) => setEditing({ ...editing, recurrence: { ...(editing.recurrence || {}), interval_n: parseInt(e.target.value || "1", 10) } })}
                          className="h-9 w-20"
                          data-testid="edit-interval"
                        />
                        <span className="text-xs text-slate-500">{editing.recurrence?.interval_unit || "Weeks"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <button onClick={remove} className="text-xs text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid="delete-meeting">
                      <Trash size={12} weight="bold" /> Delete Meeting
                    </button>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={() => setEditing(standups.find((s) => s.standup_id === selectedId) || null)} className="h-9 px-4 text-xs" data-testid="cancel-edit">
                        Cancel
                      </Button>
                      <Button onClick={save} className="h-9 px-4 bg-slate-950 hover:bg-slate-800 text-white text-xs" data-testid="save-changes">
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function hashGradient(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xfff;
  const a = h % 360;
  const b = (a + 60) % 360;
  return `linear-gradient(135deg, hsl(${a}, 70%, 55%), hsl(${b}, 65%, 60%))`;
}
